import {DialogOpenChangeDetails, DialogRootProps as ChakraDialogProps} from "@chakra-ui/react";
import {ComponentProps, ComponentType, FC, Suspense, useCallback} from "react";
import Loading from "../components/Loading";
import {useDialogDiagnostics} from "../utils/diagnostics";
import {DialogCloseTrigger, DialogContent, DialogRoot} from "../components/ui/dialog";

/** Style props that can be passed to DialogContent (excludes children and portal props). */
type DialogContentStyleProps = Omit<ComponentProps<typeof DialogContent>, "children" | "portalled" | "portalRef">;

/** Additional props passed to your component when wrapping with `withDialog`. */
export type InnerDialogProps = {
  open: boolean;
  /** Call this to close the dialog. */
  onClose: () => void;
};

/** Props added to a component when wrapped with `withDialog`. */
type WithDialogExtraProps = {
  /** Determines whether the dialog is open. */
  open: boolean;

  /** Called when the dialog is closed. */
  onClose: () => void;

  /** Called when the dialog has fully exited. */
  onExitComplete?: () => void;
};

/** These props should not be defined on the inner dialog component passed to
 * `withDialog`, because they conflict with the added outer props. */
type RestrictedDialogProps = {[K in keyof Omit<WithDialogExtraProps, keyof InnerDialogProps>]?: never};

/** The final props on the component returned by `withDialog<P>(...)` */
type WithDialogProps<P> = WithDialogExtraProps & Omit<P, keyof InnerDialogProps | keyof RestrictedDialogProps>;

/** Other props passed as a 2nd argument to `withDialog` to customize the dialog. */
type OtherDialogProps = Omit<ChakraDialogProps, keyof WithDialogExtraProps | "children"> & {
  /** Props passed to DialogContent for styling (flex, overflow, etc.) */
  contentProps?: DialogContentStyleProps;
  /** Whether the corner close button renders. Default: `true`. */
  closeTrigger?: boolean;
};

type EnsureCompatible<T, U> = U extends Pick<T, keyof T & keyof U> ? T : unknown;

export function withDialog<P>(
  WrappedComponent: ComponentType<EnsureCompatible<P, InnerDialogProps & RestrictedDialogProps>>,
  otherProps?: OtherDialogProps,
): FC<WithDialogProps<P>> {
  const {contentProps, closeTrigger = true, ...rootProps} = otherProps ?? {};

  return ({open, onClose, onExitComplete, ...rest}: WithDialogProps<P>) => {
    const diagnosticsRef = useDialogDiagnostics(open);

    const onOpenChange = useCallback(
      (details: DialogOpenChangeDetails) => {
        if (!details.open) {
          onClose.call(null);
        }
      },
      [onClose],
    );

    const innerDialogProps: InnerDialogProps = {onClose, open};

    return (
      <DialogRoot
        open={open}
        onOpenChange={onOpenChange}
        onExitComplete={onExitComplete}
        // This completely breaks sticky elemments since it adds "overflow: hidden" to the page body...
        preventScroll={false}
        {...rootProps}
      >
        <DialogContent ref={diagnosticsRef} {...contentProps}>
          <Suspense fallback={<Loading m={8} />}>
            <WrappedComponent {...(rest as unknown as any)} {...innerDialogProps} />
          </Suspense>
          {closeTrigger && <DialogCloseTrigger />}
        </DialogContent>
      </DialogRoot>
    );
  };
}
